import type { TypedDocumentNode } from "@apollo/client";
import {
  ContractTypesDocument,
  CreateContractTypeDocument,
  CreateGenderDocument,
  CreateJobDepartmentDocument,
  CreateJobLocationDocument,
  CreateMaritalStatusDocument,
  CreateWorkingTimeModelDocument,
  CreateDegreeDocument,
  DeleteDegreeDocument,
  DegreesDocument,
  UpdateDegreeEntryDocument,
  DeleteContractTypeDocument,
  DeleteGenderDocument,
  DeleteJobDepartmentDocument,
  DeleteJobLocationDocument,
  DeleteMaritalStatusDocument,
  DeleteWorkingTimeModelDocument,
  GendersDocument,
  JobDepartmentsDocument,
  JobLocationsDocument,
  MaritalStatusesDocument,
  UpdateContractTypeEntryDocument,
  UpdateGenderEntryDocument,
  UpdateJobDepartmentEntryDocument,
  UpdateJobLocationEntryDocument,
  UpdateMaritalStatusEntryDocument,
  UpdateWorkingTimeModelEntryDocument,
  CreateIndustryDocument,
  DeleteIndustryDocument,
  IndustriesDocument,
  UpdateIndustryEntryDocument,
  WorkingTimeModelsDocument,
} from "@/graphql/generated/operations";
import type {
  ContractTypesQuery,
  GendersQuery,
  JobDepartmentsQuery,
  JobLocationsQuery,
  MaritalStatusesQuery,
  IndustriesQuery,
  DegreesQuery,
  WorkingTimeModelsQuery,
} from "@/graphql/generated/operations";
import type { RefRow } from "./ReferenceDataList";

// Reference-data admin descriptors. genesis exposes six lookup types with full
// CRUD: four localizedName-backed types and two simple-name types.
//
// The UI edits one label in both cases. The descriptor owns the Genesis write
// shape, so the list component can stay generic while codegen keeps the exact
// schema-specific documents.

type ListDoc = TypedDocumentNode<unknown, Record<string, never>>;
type RefMutationVariables = Record<string, unknown>;
export type CreateRefDoc = TypedDocumentNode<unknown, RefMutationVariables>;
export type UpdateRefDoc = TypedDocumentNode<unknown, RefMutationVariables>;
export type DeleteRefDoc = TypedDocumentNode<unknown, { input: { id: string } }>;

export interface EditableRefDescriptor {
  key: string;
  labelId: string;
  subId: string;
  addPlaceholderId: string;
  emptyId: string;
  listDoc: ListDoc;
  selectRows: (data: unknown) => RefRow[];
  createDoc: CreateRefDoc;
  updateDoc: UpdateRefDoc;
  deleteDoc: DeleteRefDoc;
  createVariables: (name: string) => RefMutationVariables;
  updateVariables: (id: string, name: string) => RefMutationVariables;
}

export interface ReadonlyRefDescriptor {
  key: string;
  labelId: string;
  subId: string;
  emptyId: string;
  listDoc: ListDoc;
  selectRows: (data: unknown) => RefRow[];
}

function localizedRows(
  nodes: ReadonlyArray<{ id: string; localizedName?: { localized?: string | null } | null }>,
): RefRow[] {
  return nodes.map((n) => ({ id: n.id, label: n.localizedName?.localized ?? "" }));
}

function namedRows(nodes: ReadonlyArray<{ id: string; name?: string | null }>): RefRow[] {
  return nodes.map((n) => ({ id: n.id, label: n.name ?? "" }));
}

function localizedCreateVariables(name: string): RefMutationVariables {
  return { input: { data: { localizedName: { english: name, german: name } } } };
}

function localizedUpdateVariables(id: string, name: string): RefMutationVariables {
  return { id, input: { data: { localizedName: { english: name, german: name } } } };
}

function namedCreateVariables(name: string): RefMutationVariables {
  return { input: { data: { name } } };
}

function namedUpdateVariables(id: string, name: string): RefMutationVariables {
  return { id, input: { data: { name } } };
}

export const EDITABLE_REF_TYPES: readonly EditableRefDescriptor[] = [
  {
    key: "jobDepartment",
    labelId: "referenceData.jobDepartment.title",
    subId: "referenceData.jobDepartment.sub",
    addPlaceholderId: "referenceData.jobDepartment.add",
    emptyId: "referenceData.jobDepartment.empty",
    listDoc: JobDepartmentsDocument as unknown as ListDoc,
    selectRows: (d) => localizedRows((d as JobDepartmentsQuery).jobDepartments.jobDepartments),
    createDoc: CreateJobDepartmentDocument as unknown as CreateRefDoc,
    updateDoc: UpdateJobDepartmentEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteJobDepartmentDocument as unknown as DeleteRefDoc,
    createVariables: localizedCreateVariables,
    updateVariables: localizedUpdateVariables,
  },
  {
    key: "jobLocation",
    labelId: "referenceData.jobLocation.title",
    subId: "referenceData.jobLocation.sub",
    addPlaceholderId: "referenceData.jobLocation.add",
    emptyId: "referenceData.jobLocation.empty",
    listDoc: JobLocationsDocument as unknown as ListDoc,
    selectRows: (d) => localizedRows((d as JobLocationsQuery).jobLocations.jobLocations),
    createDoc: CreateJobLocationDocument as unknown as CreateRefDoc,
    updateDoc: UpdateJobLocationEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteJobLocationDocument as unknown as DeleteRefDoc,
    createVariables: localizedCreateVariables,
    updateVariables: localizedUpdateVariables,
  },
  {
    key: "industry",
    labelId: "referenceData.industry.title",
    subId: "referenceData.industry.sub",
    addPlaceholderId: "referenceData.industry.add",
    emptyId: "referenceData.industry.empty",
    listDoc: IndustriesDocument as unknown as ListDoc,
    selectRows: (d) => localizedRows((d as IndustriesQuery).industries.industries),
    createDoc: CreateIndustryDocument as unknown as CreateRefDoc,
    updateDoc: UpdateIndustryEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteIndustryDocument as unknown as DeleteRefDoc,
    createVariables: localizedCreateVariables,
    updateVariables: localizedUpdateVariables,
  },
  {
    key: "degree",
    labelId: "referenceData.degree.title",
    subId: "referenceData.degree.sub",
    addPlaceholderId: "referenceData.degree.add",
    emptyId: "referenceData.degree.empty",
    listDoc: DegreesDocument as unknown as ListDoc,
    selectRows: (d) => localizedRows((d as DegreesQuery).degrees.degrees),
    createDoc: CreateDegreeDocument as unknown as CreateRefDoc,
    updateDoc: UpdateDegreeEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteDegreeDocument as unknown as DeleteRefDoc,
    createVariables: localizedCreateVariables,
    updateVariables: localizedUpdateVariables,
  },
  {
    key: "contractType",
    labelId: "referenceData.contractType.title",
    subId: "referenceData.contractType.sub",
    addPlaceholderId: "referenceData.contractType.add",
    emptyId: "referenceData.contractType.empty",
    listDoc: ContractTypesDocument as unknown as ListDoc,
    selectRows: (d) => localizedRows((d as ContractTypesQuery).contractTypes.contractTypes),
    createDoc: CreateContractTypeDocument as unknown as CreateRefDoc,
    updateDoc: UpdateContractTypeEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteContractTypeDocument as unknown as DeleteRefDoc,
    createVariables: localizedCreateVariables,
    updateVariables: localizedUpdateVariables,
  },
  {
    key: "workingTimeModel",
    labelId: "referenceData.workingTimeModel.title",
    subId: "referenceData.workingTimeModel.sub",
    addPlaceholderId: "referenceData.workingTimeModel.add",
    emptyId: "referenceData.workingTimeModel.empty",
    listDoc: WorkingTimeModelsDocument as unknown as ListDoc,
    selectRows: (d) =>
      localizedRows((d as WorkingTimeModelsQuery).workingTimeModels.workingTimeModels),
    createDoc: CreateWorkingTimeModelDocument as unknown as CreateRefDoc,
    updateDoc: UpdateWorkingTimeModelEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteWorkingTimeModelDocument as unknown as DeleteRefDoc,
    createVariables: localizedCreateVariables,
    updateVariables: localizedUpdateVariables,
  },
  {
    key: "gender",
    labelId: "referenceData.gender.title",
    subId: "referenceData.gender.sub",
    addPlaceholderId: "referenceData.gender.add",
    emptyId: "referenceData.gender.empty",
    listDoc: GendersDocument as unknown as ListDoc,
    selectRows: (d) => namedRows((d as GendersQuery).genders.genders),
    createDoc: CreateGenderDocument as unknown as CreateRefDoc,
    updateDoc: UpdateGenderEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteGenderDocument as unknown as DeleteRefDoc,
    createVariables: namedCreateVariables,
    updateVariables: namedUpdateVariables,
  },
  {
    key: "maritalStatus",
    labelId: "referenceData.maritalStatus.title",
    subId: "referenceData.maritalStatus.sub",
    addPlaceholderId: "referenceData.maritalStatus.add",
    emptyId: "referenceData.maritalStatus.empty",
    listDoc: MaritalStatusesDocument as unknown as ListDoc,
    selectRows: (d) => namedRows((d as MaritalStatusesQuery).maritalStatuses.maritalStatuses),
    createDoc: CreateMaritalStatusDocument as unknown as CreateRefDoc,
    updateDoc: UpdateMaritalStatusEntryDocument as unknown as UpdateRefDoc,
    deleteDoc: DeleteMaritalStatusDocument as unknown as DeleteRefDoc,
    createVariables: namedCreateVariables,
    updateVariables: namedUpdateVariables,
  },
];

export const READONLY_REF_TYPES: readonly ReadonlyRefDescriptor[] = [];
